import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../../api";
import { timeAgo } from "../../format";

/** What the ops alerts look like on the wire (see @hive/cloud-lab ops.ts). */
export interface OpsAlert {
  id: string;
  severity: "error" | "warn";
  area: "bots" | "market-data" | "system";
  title: string;
  detail?: string;
  botId?: string;
  dataset?: { symbol: string; timeframe: string; marketType: string };
  since?: number;
}

/** One poll for every tab: the tab bar's counts and each tab's list read the same query. */
export function useOpsAlerts() {
  return useQuery({
    queryKey: ["admin", "alerts"],
    queryFn: () => api<{ items: OpsAlert[]; checkedAt: number }>("/admin/alerts"),
    refetchInterval: 30_000,
  });
}

export function bytes(n: number | null | undefined): string {
  if (n == null) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i ? 1 : 0)} ${units[i]}`;
}

export function duration(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

/** How long ago something started, as an uptime. */
export function since(at: string | number | null | undefined): string {
  if (at == null) return "—";
  const t = typeof at === "number" ? at : new Date(at).getTime();
  return duration(Math.max(0, (Date.now() - t) / 1000));
}

/**
 * A used-of-limit bar: amber from 75%, red from 90%. Without a limit it shows the
 * value alone. The numbers are text next to the bar, so the colour is never the
 * only thing that says it is nearly full.
 */
export function Meter({
  value,
  limit,
  format,
  label,
}: {
  value: number | null | undefined;
  limit: number | null | undefined;
  format: (n: number) => string;
  label: string;
}) {
  if (value == null) return <span className="muted">—</span>;
  if (!limit) return <span className="mono">{format(value)}</span>;
  const share = Math.min(1, value / limit);
  const tone = share >= 0.9 ? "ops-meter--fail" : share >= 0.75 ? "ops-meter--warn" : "";
  return (
    <span className={`ops-meter ${tone}`}>
      <span
        className="ops-meter__track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={value}
        aria-valuetext={`${format(value)} of ${format(limit)}`}
      >
        <span className="ops-meter__fill" style={{ width: `${share * 100}%` }} />
      </span>
      <span className="mono small">
        {format(value)} / {format(limit)}
      </span>
    </span>
  );
}

export type Health = "ok" | "warn" | "error" | "off";

export function HealthDot({ health, label }: { health: Health; label?: string }) {
  const cls =
    health === "error"
      ? "health-dot health-dot--fail"
      : health === "warn"
        ? "health-dot health-dot--warn"
        : health === "off"
          ? "health-dot health-dot--off"
          : "health-dot";
  const text = label ?? { ok: "OK", warn: "Warning", error: "Problem", off: "Stopped" }[health];
  return (
    <span className="bot-state">
      <span className={cls} aria-hidden="true" /> {text}
    </span>
  );
}

/**
 * The alerts for one area (or all), worst first. Says so when there are none,
 * because "nothing listed" and "not loaded" should not look the same.
 */
export function AlertList({
  items,
  loading,
  onDataset,
  showArea = false,
}: {
  items: OpsAlert[];
  loading?: boolean;
  /** Open a market's detail from its alert. */
  onDataset?: (d: NonNullable<OpsAlert["dataset"]>) => void;
  showArea?: boolean;
}) {
  if (loading) return <p className="muted">Checking…</p>;
  if (!items.length)
    return (
      <p className="ops-clear">
        <span className="health-dot" aria-hidden="true" /> All clear: nothing needs looking at.
      </p>
    );
  const areaTab = { bots: "Bots", "market-data": "Market+data", system: "Overview" } as const;
  const areaName = { bots: "Bots", "market-data": "Market data", system: "System" } as const;
  return (
    <ul className="ops-alerts">
      {items.map((a) => (
        <li key={a.id} className={`ops-alert ops-alert--${a.severity}`}>
          <span className={`badge ${a.severity === "error" ? "badge--danger" : "badge--warn"}`}>
            {a.severity === "error" ? "Problem" : "Warning"}
          </span>
          <div className="ops-alert__text">
            <strong>{a.title}</strong>
            {a.detail && <span className="muted small"> {a.detail}</span>}
            <div className="ops-alert__meta small muted">
              {showArea && (
                <>
                  <Link to={`/admin?tab=${areaTab[a.area]}`}>{areaName[a.area]}</Link>
                  {" · "}
                </>
              )}
              {a.since ? `since ${timeAgo(a.since)}` : "now"}
              {a.botId && (
                <>
                  {" · "}
                  <Link to={`/b/${a.botId}`}>Open bot</Link>
                </>
              )}
              {a.dataset && onDataset && (
                <>
                  {" · "}
                  <button type="button" className="link-btn" onClick={() => onDataset(a.dataset!)}>
                    Manage {a.dataset.symbol}
                  </button>
                </>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
